import {
  infiniteQueryOptions,
  keepPreviousData,
  queryOptions,
  type QueryClient,
} from "@tanstack/react-query";

import {
  fetchHubDocumentGroups,
  fetchHubDocuments,
  fetchHubOpportunities,
  fetchHubOpportunityFacets,
  fetchHubOpportunityGroups,
  fetchHubTabCounts,
  type HubDocumentCursors,
  type HubDocumentGroupsPage,
  type HubDocumentRow,
  type HubOpportunitiesPage,
  type HubOpportunityGroupBy,
  type HubOpportunityGroupsPage,
  type HubOpportunityListFilters,
} from "@/features/persona-opportunities/api/persona-opportunity-hub.api";
import { fetchOrders } from "@/features/marketplace/api/marketplace.api";
import type { TenantOrdersPageResponse } from "@/features/marketplace/types/marketplace.types";

import { marketplaceKeys, MARKETPLACE_STALE_TIME_MS } from "./marketplace-query-keys";
import { normalizedPersonaOpportunitySetTenantId } from "./persona-opportunity-sets.queries";

/**
 * Every hub tab loads one page of what is on screen. Further pages follow the
 * scroll window, so a tenant with hundreds of angles does not pay for the rest.
 */
export const HUB_PAGE_SIZE = 50;

function setScope(setIds: readonly string[]): string {
  return [...setIds].sort().join(",");
}

export const personaOpportunityHubKeys = {
  all: ["persona-opportunity-hub"] as const,
  /** Selection is order-insensitive: the same sets must not produce two caches. */
  opportunities: (
    tenantId: string,
    setIds: readonly string[],
    scope: string,
  ) => ["persona-opportunity-hub", "opportunities", tenantId, setScope(setIds), scope] as const,
  groups: (tenantId: string, setIds: readonly string[], scope: string) =>
    ["persona-opportunity-hub", "groups", tenantId, setScope(setIds), scope] as const,
  facets: (tenantId: string, setIds: readonly string[], dataset: string) =>
    ["persona-opportunity-hub", "facets", tenantId, setScope(setIds), dataset] as const,
  /** Rows of expanded Scripts & Briefs groups. */
  documentsRoot: ["persona-opportunity-hub", "documents"] as const,
  documentGroupRows: (tenantId: string, opportunityId: string) =>
    ["persona-opportunity-hub", "documents", tenantId, opportunityId] as const,
  documentGroups: (
    tenantId: string,
    setIds: readonly string[],
    opportunityIds: readonly string[],
  ) =>
    [
      "persona-opportunity-hub",
      "document-groups",
      tenantId,
      setScope(setIds),
      setScope(opportunityIds),
    ] as const,
  tabCounts: (tenantId: string, setIds: readonly string[]) =>
    ["persona-opportunity-hub", "tab-counts", tenantId, setScope(setIds)] as const,
} as const;

/**
 * One opportunity holds a slot-bounded handful of scripts and briefs, so a
 * group reads at the server's page cap and rarely needs a second request.
 */
const HUB_DOCUMENT_GROUP_PAGE_SIZE = 200;

function mapHubDocument(
  queryClient: QueryClient,
  documentId: string,
  mapItem: (item: HubDocumentRow) => HubDocumentRow,
) {
  queryClient.setQueriesData<HubDocumentRow[]>(
    { queryKey: personaOpportunityHubKeys.documentsRoot },
    (current) => current?.map((item) => (item.id === documentId ? mapItem(item) : item)),
  );
}

/** Keep the hub scripts list in step with a star toggle before the documents refetch. */
export function patchHubDocumentFavorite(
  queryClient: QueryClient,
  documentId: string,
  isFavorite: boolean,
) {
  mapHubDocument(queryClient, documentId, (item) => ({ ...item, isFavorite }));
}

/** Keep the hub scripts list title in step with a hook edit before the documents refetch. */
export function patchHubDocumentTitle(
  queryClient: QueryClient,
  documentId: string,
  title: string | null,
) {
  mapHubDocument(queryClient, documentId, (item) => ({ ...item, title }));
}

export interface HubOpportunityQueryScope {
  groupBy?: HubOpportunityGroupBy;
  groupKey?: string;
  filters?: HubOpportunityListFilters;
}

function scopeKey(scope: HubOpportunityQueryScope): string {
  return JSON.stringify({
    groupBy: scope.groupBy ?? null,
    groupKey: scope.groupKey ?? null,
    filters: scope.filters ?? null,
  });
}

/** Angles across the selected sets — one group when `groupKey` is set. */
export function hubOpportunitiesInfiniteQueryOptions(
  tenantId: string | null | undefined,
  setIds: readonly string[],
  scope: HubOpportunityQueryScope = {},
  options: { enabled?: boolean } = {},
) {
  const normalizedTenant = normalizedPersonaOpportunitySetTenantId(tenantId);
  return infiniteQueryOptions({
    queryKey: personaOpportunityHubKeys.opportunities(
      normalizedTenant,
      setIds,
      scopeKey(scope),
    ),
    queryFn: ({ pageParam }): Promise<HubOpportunitiesPage> =>
      fetchHubOpportunities({
        setIds,
        cursor: pageParam,
        limit: HUB_PAGE_SIZE,
        groupBy: scope.groupBy,
        groupKey: scope.groupKey,
        filters: scope.filters,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: Boolean(tenantId) && (options.enabled ?? true),
  });
}

/** Collapsed group headers. Children are a separate query per expanded group. */
export function hubOpportunityGroupsInfiniteQueryOptions(
  tenantId: string | null | undefined,
  setIds: readonly string[],
  scope: HubOpportunityQueryScope & { groupBy: HubOpportunityGroupBy },
  options: { enabled?: boolean } = {},
) {
  const normalizedTenant = normalizedPersonaOpportunitySetTenantId(tenantId);
  return infiniteQueryOptions({
    queryKey: personaOpportunityHubKeys.groups(normalizedTenant, setIds, scopeKey(scope)),
    queryFn: ({ pageParam }): Promise<HubOpportunityGroupsPage> =>
      fetchHubOpportunityGroups({
        setIds,
        groupBy: scope.groupBy,
        cursor: pageParam,
        limit: HUB_PAGE_SIZE,
        filters: scope.filters,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: Boolean(tenantId) && (options.enabled ?? true),
  });
}

/** Distinct no-persona facets. `dataset` of `all` leaves the other lists unscoped. */
export function hubOpportunityFacetsQueryOptions(
  tenantId: string | null | undefined,
  setIds: readonly string[],
  dataset: string,
  options: { enabled?: boolean } = {},
) {
  const normalizedTenant = normalizedPersonaOpportunitySetTenantId(tenantId);
  return queryOptions({
    queryKey: personaOpportunityHubKeys.facets(normalizedTenant, setIds, dataset),
    queryFn: () =>
      fetchHubOpportunityFacets({
        setIds,
        dataset: dataset === "all" ? undefined : dataset,
      }),
    retry: false,
    enabled: Boolean(tenantId) && (options.enabled ?? true),
  });
}

/** Scripts & Briefs opportunity headers. Rows are a separate query per expanded group. */
export function hubDocumentGroupsInfiniteQueryOptions(
  tenantId: string | null | undefined,
  setIds: readonly string[],
  opportunityIds: readonly string[],
  options: { enabled?: boolean } = {},
) {
  const normalizedTenant = normalizedPersonaOpportunitySetTenantId(tenantId);
  return infiniteQueryOptions({
    queryKey: personaOpportunityHubKeys.documentGroups(normalizedTenant, setIds, opportunityIds),
    queryFn: ({ pageParam }): Promise<HubDocumentGroupsPage> =>
      fetchHubDocumentGroups({
        setIds,
        opportunityIds,
        offset: pageParam,
        limit: HUB_PAGE_SIZE,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => lastPage.nextOffset ?? undefined,
    enabled: Boolean(tenantId) && (options.enabled ?? true),
  });
}

/** Every script and brief of one opportunity, following the two keyset cursors to the end. */
export function hubDocumentGroupRowsQueryOptions(
  tenantId: string | null | undefined,
  opportunityId: string,
) {
  const normalizedTenant = normalizedPersonaOpportunitySetTenantId(tenantId);
  return queryOptions({
    queryKey: personaOpportunityHubKeys.documentGroupRows(normalizedTenant, opportunityId),
    queryFn: async (): Promise<HubDocumentRow[]> => {
      const rows: HubDocumentRow[] = [];
      let cursors: HubDocumentCursors = {};
      for (;;) {
        const page = await fetchHubDocuments({
          setIds: [],
          opportunityId,
          limit: HUB_DOCUMENT_GROUP_PAGE_SIZE,
          ...cursors,
        });
        rows.push(...page.items);
        if (!page.hasMore) return rows;
        cursors = {
          scriptCursor: page.scriptCursor ?? undefined,
          briefCursor: page.briefCursor ?? undefined,
        };
      }
    },
    enabled: Boolean(tenantId),
  });
}

/** Exact breadcrumb totals. Enabled for the tenant, not only the open tab. */
export function hubTabCountsQueryOptions(
  tenantId: string | null | undefined,
  setIds: readonly string[],
) {
  const normalizedTenant = normalizedPersonaOpportunitySetTenantId(tenantId);
  return queryOptions({
    queryKey: personaOpportunityHubKeys.tabCounts(normalizedTenant, setIds),
    queryFn: () => fetchHubTabCounts({ setIds }),
    enabled: Boolean(tenantId),
    placeholderData: keepPreviousData,
    meta: { skipGlobalError: true },
  });
}

/** Production orders on the hub's live sets. An empty selection is every live set. */
export function hubOrdersInfiniteQueryOptions(
  tenantId: string | null | undefined,
  setIds: readonly string[],
  options: { enabled?: boolean } = {},
) {
  const tid = tenantId ?? "";
  return infiniteQueryOptions({
    queryKey: marketplaceKeys.ordersForSets(tid || "__none__", setIds),
    queryFn: ({ pageParam }): Promise<TenantOrdersPageResponse> =>
      fetchOrders(tid, {
        hubSetScope: true,
        ...(setIds.length > 0 ? { opportunitySetIds: setIds } : {}),
        limit: HUB_PAGE_SIZE,
        offset: pageParam,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      const loaded = lastPage.offset + lastPage.orders.length;
      return loaded < lastPage.total ? loaded : undefined;
    },
    enabled: Boolean(tid) && (options.enabled ?? true),
    staleTime: MARKETPLACE_STALE_TIME_MS,
  });
}
